import React, { useEffect, useState, useRef } from "react"
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Collapsible from "@radix-ui/react-collapsible"
import { seeMoreButton } from "./SeeMore.css"

export type SeeMoreProps = {
  children: React.ReactNode
  className?: string
  width?: string | number
  lineClamp?: number
  onToggle?: (open: boolean) => void
  isList?: boolean
}

export function SeeMore({
  children,
  className,
  width = "100%",
  lineClamp = 1,
  onToggle,
  isList = false,
}: SeeMoreProps) {
  const [open, setOpen] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isList) {
      if (contentRef.current) {
        const listItems = contentRef.current.querySelectorAll("li")
        setIsOverflowing(listItems.length > 1)
      }
    } else {
      if (open) {
        setIsOverflowing(false)
        return
      }
      if (contentRef.current) {
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } =
          contentRef.current
        const overflowV = scrollHeight > clientHeight
        const overflowH = scrollWidth > clientWidth
        setIsOverflowing(overflowV || overflowH)
      }
    }
  }, [open, children, isList])

  const shouldShowButton = isList
    ? isOverflowing || open
    : open || isOverflowing

  const baseStyle: React.CSSProperties = isList
    ? { width }
    : {
        width,
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: open ? "unset" : lineClamp,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
      }

  // When in list mode, add a special class name so that our CSS can target it.
  const containerClassName = isList
    ? `seeMoreList ${className || ""}`.trim()
    : className

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        onToggle && onToggle(o)
      }}
    >
      <Collapsible.Content forceMount>
        <div
          ref={contentRef}
          className={containerClassName}
          data-open={open ? "true" : "false"}
          style={baseStyle}
        >
          {children}
        </div>
      </Collapsible.Content>

      {shouldShowButton && (
        <Collapsible.Trigger asChild>
          <button
            onClick={() => onToggle && onToggle(!open)}
            className={seeMoreButton}
          >
            {open ? "See less" : "See more"}
          </button>
        </Collapsible.Trigger>
      )}
    </Collapsible.Root>
  )
}
